// Die Regeln des Feeds — ohne Netz, damit Seite und Vorlage dieselben sind.
//
// Aus einem Eintrag (Meldung, Fall, Termin, Einschätzung, Idee) wird eine
// ZEILE der Tafel: Zeit, Titel, darunter die Art mit dem Betrifft, rechts
// der Wert. Aus den Zeilen werden TAGE, in denen die Termine oben stehen.

import type { Assessment, MarketEvent, NewsItem, ResearchCase,
              TradeIdeaDetail } from '../api/client'
import { STATUS_TEXTE } from '../pages/eventVocabulary'
import { label } from '../pages/researchVocabulary'
import { STATUS as IDEE_STATUS } from '../pages/tradeIdeaVocabulary'
import type { Ton } from './faelleRegeln'
import { wirkungTon } from './faelleRegeln'
import { alsTag, neuSeit } from './uebersichtRegeln'

/** Die fünf Kanäle in Worten — Seite, Kopfzeile und Hinweise sprechen dieselben. */
export const KANAL_LABEL: Record<string, string> = {
  news: 'News', research: 'Recherche', event: 'Termine',
  assessment: 'Ampel', trade_idea: 'Ideen',
}

const STUFEN: Record<Assessment['rating'], string> = {
  GREEN: 'grün', AMBER: 'gelb', RED: 'rot',
}

/** Wie viele Ziele in der Zeile stehen; der Rest wird zu „+n". */
const MAX_ZIELE = 3

/**
 * Die Einschätzung, so weit der Feed sie liest.
 *
 * `Assessment` ist in client.ts zweimal deklariert und wird zu EINEM Typ
 * zusammengeführt — samt Kurs, Verfassung und Gründen der Reports. Der Feed
 * braucht davon nur die Ampel.
 */
export type FeedEinschaetzung = Pick<Assessment, 'id' | 'rating' | 'headline'
                                              | 'targets' | 'assessed_at'>

/** Ein Eintrag, wie `useEventFeed` ihn liefert — das Nötigste davon. */
export interface FeedEintrag {
  key: string
  /** Wann es ERSCHIEN, nicht wann der Hinweis eintraf. */
  at: Date
  topic: string
  id: number | null
  news?: NewsItem
  case?: ResearchCase
  event?: MarketEvent
  assessment?: FeedEinschaetzung
  idea?: TradeIdeaDetail
  /** Warum nichts nachgeladen werden konnte. */
  note?: string
  /** Seit dem letzten Rendern dazugekommen — für die kurze Hervorhebung. */
  fresh?: boolean
}

/** Eine Zeile der Tafel. */
export interface FeedZeile {
  key: string
  at: Date
  /** „12:03" — bei einem Termin „—", er hat keine Uhrzeit. */
  zeit: string
  /** „Montag, 7. September 2026, 12:03 MESZ" — beim Zeigen auf die Zeit. */
  zeitVoll: string
  titel: string
  /** Wohin der Titel führt, wenn es eine Seite dafür gibt. */
  to: string | null
  /** „News", „Case · Update · laufend", „Ampel · gelb", „Termin · heute, bestätigt". */
  art: string
  /** Die ersten drei Ziele mit Namen, „+n" für den Rest. */
  betrifft: string
  /** Rechts: „+2", „W3", „gilt" — leer bei Ampel und Termin. */
  wert: string
  wertTon: Ton
  /** Der Wert ausgeschrieben, beim Zeigen: „Score +2 von ±3". */
  wertVoll: string
  /** Bei einer Einschätzung steht rechts die Stufe als Punkt, keine Zahl. */
  ampel: Assessment['rating'] | null
  termin: boolean
  /** Seit gestern 18:00 erschienen — die Kante links. Termine nie. */
  neu: boolean
  fresh: boolean
  /** Der Datensatz ist noch unterwegs. */
  laedt: boolean
}

/** Ein Tag der Tafel: die Termine oben, darunter die Einträge. */
export interface FeedGruppe {
  /** JJJJ-MM-TT in Ortszeit. */
  key: string
  /** „Heute", „Gestern", sonst der Wochentag. */
  label: string
  /** „Montag, 7. September". */
  sub: string
  /** „6 Einträge · 2 Termine · 4 neu". */
  rechts: string
  termine: FeedZeile[]
  zeilen: FeedZeile[]
}

// ---------------------------------------------------------------- Zeit

const UHR = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
const TAG_LANG = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
})
const TAG_SUB = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long', day: 'numeric', month: 'long',
})
const TAG_OHNE_WOCHENTAG = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric', month: 'long',
})
const TAG_MIT_JAHR = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric', month: 'long', year: 'numeric',
})
const WOCHENTAG = new Intl.DateTimeFormat('de-DE', { weekday: 'long' })
const ZONE = new Intl.DateTimeFormat('de-DE', { timeZoneName: 'short' })

/** 12:03 */
export function uhr(d: Date): string { return UHR.format(d) }

function zone(d: Date): string {
  return ZONE.formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/** „Montag, 7. September 2026, 12:03 MESZ" — ohne Uhrzeit: „…, Uhrzeit offen". */
export function voll(d: Date, mitUhr = true): string {
  return mitUhr
    ? `${TAG_LANG.format(d)}, ${uhr(d)} ${zone(d)}`.trim()
    : `${TAG_LANG.format(d)}, Uhrzeit offen`
}

function nullUhr(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** JJJJ-MM-TT → Ortszeit-Datum. Nicht `new Date(iso)`: Das wäre UTC. */
function tagAus(tag: string): Date {
  const [j, m, t] = tag.split('-').map(Number)
  return new Date(j, m - 1, t)
}

function tageZurueck(tag: Date, heute: Date): number {
  return Math.round(
    (nullUhr(heute).getTime() - nullUhr(tag).getTime()) / 86_400_000)
}

/**
 * „Heute" und „Gestern" statt des Datums, sonst der Wochentag.
 *
 * Auf einem Bildschirm, der tagelang läuft, ist das die Frage — nicht der
 * Kalendertag. Der steht daneben.
 */
export function tagLabel(tag: Date, heute: Date = new Date()): string {
  const abstand = tageZurueck(tag, heute)
  if (abstand === 0) return 'Heute'
  if (abstand === 1) return 'Gestern'
  return WOCHENTAG.format(tag)
}

/**
 * Das Datum neben dem Label: „Montag, 7. September" zu Heute und Gestern;
 * bei älteren Tagen steht der Wochentag schon im Label, also nur
 * „5. September" — und aus einem anderen Jahr mit Jahreszahl.
 */
export function tagSub(tag: Date, heute: Date = new Date()): string {
  if (tageZurueck(tag, heute) <= 1) return TAG_SUB.format(tag)
  return tag.getFullYear() === heute.getFullYear()
    ? TAG_OHNE_WOCHENTAG.format(tag) : TAG_MIT_JAHR.format(tag)
}

/** „1 Eintrag", „6 Einträge" */
export function zaehl(n: number, ein: string, mehr: string): string {
  return `${n} ${n === 1 ? ein : mehr}`
}

// -------------------------------------------------------------- Zeilen

function betrifftAus(ziele: { name: string | null; ref: string }[],
                     leer: string): string {
  if (ziele.length === 0) return leer
  const namen = ziele.slice(0, MAX_ZIELE).map((z) => z.name ?? z.ref)
  const rest = ziele.length - namen.length
  return namen.join(' · ') + (rest > 0 ? ` +${rest}` : '')
}

/**
 * Was am Fall geschah — aus dem Fall selbst, ohne seinen Verlauf.
 *
 * Der Feed hält den FALL, nicht die Bewegung: Der Ereignisstrom nennt nur
 * seine Kennung. Angelegt am selben Tag heißt „Neuer Fall", ein Ende heißt
 * „Beendet", alles andere ist ein „Update". Eine geänderte Wahrscheinlichkeit
 * sähe man nur im Verlauf; dafür gibt es die Fälle-Ansicht.
 */
function ereignisAusFall(f: ResearchCase, at: Date): string {
  if (f.status.startsWith('RESOLVED_') || f.status === 'CLOSED') return 'Beendet'
  if (alsTag(new Date(f.created)) === alsTag(at)) return 'Neuer Fall'
  return 'Update'
}

/** Der Stand einer Idee, so kurz, dass er in die Wertspalte passt. */
const IDEE_KURZ: Record<string, string> = {
  PROPOSED: 'vorg.', ACTIVE: 'angen.', ENTERED: 'im Markt',
  INVALIDATED: 'ungültig', EXPIRED: 'abgelaufen', CLOSED: 'abgeschl.',
  REJECTED: 'abgelehnt',
}

function ideeTon(status: string): Ton {
  if (status === 'ACTIVE' || status === 'ENTERED') return 'good'
  if (status === 'INVALIDATED') return 'bad'
  if (status === 'EXPIRED') return 'warn'
  return 'dim'
}

/** Aus einem Eintrag eine Zeile. `seit` ist die Grenze für „neu". */
export function zeileAus(e: FeedEintrag, seit: Date): FeedZeile {
  const basis: FeedZeile = {
    key: e.key, at: e.at,
    zeit: uhr(e.at), zeitVoll: voll(e.at),
    titel: '', to: null, art: KANAL_LABEL[e.topic] ?? e.topic, betrifft: '',
    wert: '', wertTon: 'dim', wertVoll: '',
    ampel: null, termin: false,
    neu: e.at.getTime() >= seit.getTime(),
    fresh: Boolean(e.fresh), laedt: false,
  }

  if (e.news) {
    const s = e.news.sentiment
    return {
      ...basis, titel: e.news.headline, art: 'News',
      betrifft: betrifftAus(e.news.targets, 'ohne Bezug'),
      // Ein echtes Minus (U+2212), nicht der Bindestrich: In der Monospace
      // ist das der Strich, der so breit ist wie das Plus.
      wert: s > 0 ? `+${s}` : s < 0 ? `−${Math.abs(s)}` : '±0',
      wertTon: s > 0 ? 'good' : s < 0 ? 'bad' : 'dim',
      wertVoll: `Score ${s > 0 ? '+' : ''}${s} von ±3`,
    }
  }

  if (e.case) {
    const f = e.case
    const ereignis = ereignisAusFall(f, e.at)
    const stand = label('case_status', f.status)
    return {
      ...basis, titel: f.title, to: `/research/${f.id}`,
      art: `Case · ${ereignis} · ${stand}`,
      betrifft: betrifftAus(
        f.instruments.map((i) => ({ name: i.name, ref: i.isin })), 'ohne Titel'),
      wert: `W${f.regime_impact}`, wertTon: wirkungTon(f.regime_impact),
      wertVoll: `${ereignis} · Wirkung ${f.regime_impact} von 4 · ${stand}`,
    }
  }

  if (e.event) {
    const t = e.event
    const status = STATUS_TEXTE[t.status] ?? t.status
    return {
      // Ein Termin hat einen Tag, keine Uhrzeit — und ist nie „neu": Er
      // steht an, er ist nicht erschienen.
      ...basis, termin: true, neu: false,
      zeit: '—', zeitVoll: voll(e.at, false),
      titel: t.title, art: `Termin · heute, ${status}`,
      betrifft: betrifftAus(t.targets, 'Markt'),
      wertVoll: `Termin heute, ${status}`,
    }
  }

  if (e.assessment) {
    const a = e.assessment
    return {
      ...basis, titel: a.headline, art: `Ampel · ${STUFEN[a.rating]}`,
      betrifft: betrifftAus(a.targets, 'Markt'),
      ampel: a.rating, wertVoll: `Ampel ${STUFEN[a.rating]}`,
    }
  }

  if (e.idea) {
    const i = e.idea
    // Was passiert ist: die jüngste Fortschreibung, wenn die Zeile auf ihr
    // steht — sonst die Anlage. Derselbe Maßstab wie beim Zeitpunkt im Haken.
    const juengste = i.updates[0]
    const fortgeschrieben = juengste !== undefined
      && new Date(juengste.recorded_at).getTime() === e.at.getTime()
    return {
      ...basis,
      titel: fortgeschrieben
        ? `${i.headline}: ${juengste.headline}`
        : `Neue Idee: ${i.headline}`,
      art: 'Idee', betrifft: i.name ?? i.isin ?? '—',
      wert: IDEE_KURZ[i.status] ?? i.status, wertTon: ideeTon(i.status),
      wertVoll: `Idee ${IDEE_STATUS[i.status] ?? i.status}`,
    }
  }

  if (e.note) {
    // Ein Hinweis ohne Kennung: Es ist etwas geschehen, aber nichts
    // nachzuladen. Verschwiegen wäre es ein Verlust, den niemand bemerkt.
    return { ...basis, titel: `${KANAL_LABEL[e.topic] ?? e.topic} geändert`,
             betrifft: e.note }
  }

  // Der Datensatz ist unterwegs. Die Zeile steht schon, damit die Reihenfolge
  // nicht springt, wenn zwei Abrufe unterschiedlich lange brauchen.
  return { ...basis, titel: 'lädt …', laedt: true }
}

// ---------------------------------------------------------------- Tage

/**
 * Die Einträge als Tage, jüngster zuerst; in jedem Tag die Termine oben.
 *
 * Nach dem LOKALEN Tag: Eine Meldung von 23:30 gehört zum heutigen Abend
 * und nicht zum morgigen Vormittag.
 */
export function gruppenAus(eintraege: FeedEintrag[], seit: Date = neuSeit(),
                           heute: Date = new Date()): FeedGruppe[] {
  const tage = new Map<string, FeedZeile[]>()
  for (const e of eintraege) {
    const z = zeileAus(e, seit)
    const tag = alsTag(z.at)
    const liste = tage.get(tag)
    if (liste) liste.push(z)
    else tage.set(tag, [z])
  }

  return [...tage.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([tag, zeilen]) => {
      const termine = zeilen.filter((z) => z.termin)
      const rest = zeilen.filter((z) => !z.termin)
        .sort((a, b) => b.at.getTime() - a.at.getTime())
      const neu = rest.filter((z) => z.neu).length
      const datum = tagAus(tag)
      const rechts = [
        zaehl(rest.length, 'Eintrag', 'Einträge'),
        termine.length ? zaehl(termine.length, 'Termin', 'Termine') : '',
        neu ? `${neu} neu` : '',
      ].filter(Boolean).join(' · ')
      return { key: tag, label: tagLabel(datum, heute), sub: tagSub(datum, heute),
               rechts, termine, zeilen: rest }
    })
}

/** „20 Einträge · 4 Tage" — links im Fuß der Tafel. */
export function fussAus(gruppen: FeedGruppe[]): string {
  const anzahl = gruppen.reduce((n, g) => n + g.termine.length + g.zeilen.length, 0)
  return `${zaehl(anzahl, 'Eintrag', 'Einträge')} · ${zaehl(gruppen.length, 'Tag', 'Tage')}`
}
